/**
 * Text cleanup before speech. Shared by the live sentence pipeline and the
 * clip fallback so both read symbols the same way.
 */

const MAGNITUDE: Record<string, string> = {
  K: 'thousand',
  M: 'million',
  B: 'billion',
  T: 'trillion',
}

// "down -4%" already says the direction, so the sign is not spoken again.
const DIRECTION_BEFORE_NEGATIVE_PERCENT = new RegExp(
  '(?<![\\p{L}\\p{N}_])(down|fell|lost|dropped|lower|declined|slid|sank|bajó|cayó|perdió|retrocedió|baja|cae)(\\s+)-(?=\\d[\\d,]*(?:\\.\\d+)?\\s?%)',
  'giu',
)

/**
 * Strip the markdown a voice cannot read and say symbols as words. Table
 * rows are dropped entirely: a pipe-row read aloud is noise, and voice
 * answers should not contain tables in the first place.
 *
 * Cashtags become plain tickers ($TCRT -> TCRT), $3.17 -> "3.17 dollars",
 * 5.39% -> "5.39 percent". Bare decimals and dotted tickers (BRK.B) stay.
 */
export function stripSpokenMarkdown(text: string): string {
  // Fenced blocks (```chart data, code) are for the eye, never the ear.
  let out = text.replace(/```[\s\S]*?(```|$)/g, '')
  // Whole-line constructs first.
  out = out
    .split('\n')
    .filter((line) => !/^\s*\|.*\|\s*$/.test(line)) // table rows
    .filter((line) => !/^\s*[-=]{3,}\s*$/.test(line)) // rules
    .map((line) => line.replace(/^\s*#{1,6}\s+/, '')) // headers
    .map((line) => line.replace(/^\s*[-*•]\s+/, '')) // bullets
    .join('\n')
  out = out.replace(/\[POINT:[^\]]*\]/g, '') // pointer tags are for the cursor, not the ear
  out = out.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> text
  out = out.replace(/\*\*([^*]*)\*\*/g, '$1') // bold
  out = out.replace(/\*([^*]*)\*/g, '$1') // italics
  // Underscore emphasis only at word edges, so snake_case names survive.
  out = out.replace(/(^|[^\w])__([^_\n]+)__(?=[^\w]|$)/g, '$1$2')
  out = out.replace(/(^|[^\w])_([^_\n]+)_(?=[^\w]|$)/g, '$1$2')
  out = out.replace(/`([^`]*)`/g, '$1') // inline code
  // Money before cashtags: "$3.17", "$1.2M", "$1,250", "$5k". The lookahead
  // refuses a number run into letters ("$1.2Mx"), so it cannot backtrack into
  // a shorter number and mangle it.
  out = out.replace(
    /\$(\d[\d,]*(?:\.\d+)?)([KMBTkmbt])?(?![.\d]*[A-Za-z0-9])/g,
    (_m, num: string, mag?: string) =>
      `${num} ${mag ? `${MAGNITUDE[mag.toUpperCase()]} ` : ''}${num === '1' && !mag ? 'dollar' : 'dollars'}`,
  )
  out = out.replace(/\$([A-Z]{1,5}(?:\.[A-Z])?)(?![A-Za-z])/g, '$1') // cashtags
  out = out.replace(DIRECTION_BEFORE_NEGATIVE_PERCENT, '$1$2')
  // A leading minus is a sign only when not glued to a preceding word ("5-10%").
  out = out.replace(
    /(^|[^\w])(-)?(\d[\d,]*(?:\.\d+)?)\s?%/g,
    (_m, pre: string, neg: string | undefined, num: string) =>
      `${pre}${neg ? 'minus ' : ''}${num} percent`,
  )
  out = out.replace(/[ \t]+/g, ' ')
  return out
}
